import React, { useState, useEffect, useRef } from "react";
import { IoAdd } from "react-icons/io5";

const useProfileValidation = (profiles) => {
    const [error, setError] = useState("");
    const [nameErrors, setNameErrors] = useState(Array(profiles.length).fill(false));
    const [ageErrors, setAgeErrors] = useState(Array(profiles.length).fill(""));

    const validateName = (name, index) => {
        const isValid = name.trim() !== "";
        setNameErrors(prev => prev.map((err, i) => i === index ? !isValid : err));
        return isValid;
    };

    const validateAge = (age, index) => {
        if (age.trim() === "") {
            setAgeErrors(prev => prev.map((err, i) => (i === index ? "Age is required" : err)));
            return false;
        }
        const ageNum = parseInt(age);
        if (isNaN(ageNum) || ageNum < 0 || ageNum > 120) {
            setAgeErrors(prev => prev.map((err, i) => (i === index ? "Enter a valid age (0-120)" : err)));
            return false;
        }
        setAgeErrors(prev => prev.map((err, i) => (i === index ? "" : err)));
        return true;
    };

    return { error, setError, nameErrors, setNameErrors, ageErrors, setAgeErrors, validateName, validateAge };
};

const AvatarSlider = ({ index, onAvatarChange, centeredAvatars, sliderRef }) => (
    <div
        ref={sliderRef}
        className="w-screen flex overflow-x-auto gap-6 py-2 scrollbar-hide"
        style={{
            marginLeft: "calc(-50vw + 50%)",
            marginRight: "calc(-50vw + 50%)",
            paddingLeft: "calc(50% - 10px)",
            paddingRight: "calc(50% - 10px)",
            scrollSnapType: "x mandatory",
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" }
        }}
    >
        {Array(8).fill("").map((_, idx) => (
            <button
                key={idx}
                style={{ scrollSnapAlign: "center" }}
                className={`h-16 w-16 rounded-full flex-shrink-0 transition-transform duration-300 ${
                    centeredAvatars[index] === idx
                        ? "border border-[#E1E1E1] scale-125"
                        : "opacity-50 scale-100 border border-[#E1E1E1]"
                }`}
                onClick={() => onAvatarChange(index, `/images/avatar${idx}.png`, idx)}
            >
                <img
                    src={`/images/avatar${idx}.png`}
                    alt={`Avatar ${idx + 1}`}
                    className="h-full w-full rounded-full object-cover p-2"
                />
            </button>
        ))}
    </div>
);

const AddFamily = ({ onBack, onComplete, isEditMode = false }) => {
    const [profiles, setProfiles] = useState([
        { name: "", age: "", avatar: "/images/avatar2.png" },
        { name: "", age: "", avatar: "/images/avatar6.png" }
    ]);
    const [centeredAvatars, setCenteredAvatars] = useState([2, 6]);
    const sliderRefs = useRef([]);
    const MAX_PROFILES = 5;

    const {
        error,
        setError,
        nameErrors,
        setNameErrors,
        ageErrors,
        setAgeErrors,
        validateName,
        validateAge
    } = useProfileValidation(profiles);

    useEffect(() => {
        const initializeScroll = () => {
            sliderRefs.current.forEach((slider, index) => {
                if (slider) {
                    const avatarWidth = 64;
                    const gap = 24;
                    const scrollAmount = index === 0
                        ? avatarWidth * 2 + gap * 2
                        : avatarWidth * 4 + gap * 4;
                    slider.scrollLeft = scrollAmount;
                }
            });
        };

        const handleScroll = (index) => {
            const slider = sliderRefs.current[index];
            if (!slider) return;

            const sliderRect = slider.getBoundingClientRect();
            const sliderCenter = sliderRect.left + sliderRect.width / 2;

            const closestAvatar = Array.from(slider.children).reduce((closest, child, idx) => {
                const childRect = child.getBoundingClientRect();
                const distance = Math.abs(sliderCenter - (childRect.left + childRect.width / 2));
                return distance < closest.distance ? { index: idx, distance } : closest;
            }, { index: 0, distance: Infinity });

            setCenteredAvatars(prev => prev.map((value, i) => i === index ? closestAvatar.index : value));
        };

        initializeScroll();

        sliderRefs.current.forEach((slider, index) => {
            if (slider) {
                const listener = () => handleScroll(index);
                slider.addEventListener('scroll', listener);
                slider.addEventListener('touchmove', listener);
                return () => {
                    slider.removeEventListener('scroll', listener);
                    slider.removeEventListener('touchmove', listener);
                };
            }
        });
    }, []);

    const handleAddProfile = () => {
        if (profiles.length >= MAX_PROFILES) {
            setError(`Maximum limit of ${MAX_PROFILES} profiles reached.`);
            return;
        }
        setProfiles([...profiles, { name: "", age: "", avatar: "/images/avatar.png" }]);
        setCenteredAvatars([...centeredAvatars, null]);
        setAgeErrors([...ageErrors, ""]);
        setNameErrors([...nameErrors, false]);
        setError("");
    };

    const handleDeleteProfile = (index) => {
        if (profiles.length <= 2) {
            setError("You must have at least 2 profiles.");
            return;
        }
        setProfiles(profiles.filter((_, i) => i !== index));
        setCenteredAvatars(centeredAvatars.filter((_, i) => i !== index));
        setAgeErrors(ageErrors.filter((_, i) => i !== index));
        setError("");
    };

    const handleAvatarChange = (index, newAvatar, avatarIndex) => {
        setProfiles(prev => prev.map((profile, i) =>
            i === index ? { ...profile, avatar: newAvatar } : profile
        ));
        setCenteredAvatars(prev => prev.map((value, i) =>
            i === index ? avatarIndex : value
        ));
    };

    const handleAgeChange = (e, index) => {
        const newAge = e.target.value;
        if (newAge === "" || /^\d*$/.test(newAge)) {
            setProfiles(prev => prev.map((p, i) =>
                i === index ? { ...p, age: newAge } : p
            ));
            validateAge(newAge, index);
        }
    };

    const handleSave = () => {
        let hasErrors = false;

        profiles.forEach((profile, index) => {
            if (!validateName(profile.name, index)) {
                setError("Please fill out all name fields.");
                hasErrors = true;
            }
            if (!validateAge(profile.age, index)) {
                hasErrors = true;
            }
        });

        if (!hasErrors) {
            setError("");
            onComplete();
        }
    };

    return (
        <div className={`min-h-screen flex flex-col gap-6 pt-[26px] pr-[10px] pb-[11px] pl-[20px] ${
            isEditMode ? "bg-gradient-to-b from-pink-200 via-white to-[#FAFAFA] from-0% via-20% to-100%" : ""
        }`}>
            <div className="w-full flex items-center justify-between">
                <button
                    onClick={onBack}
                    style={{top: isEditMode ? '34px' : ''}}
                    className="absolute bg-white rounded-full p-1 shadow-[2px_2px_4px_rgba(0,0,0,0.05),_5px_5px_2px_rgba(0,0,0,0.15)]"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-gray-400"
                        fill="none"
                        viewBox="0 0 26 26"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                {isEditMode ? (
                    <h1 className="text-[20px] leading-[20px] font-normal font-young-serif text-[#3A1A1A] pt-4 text-center m mx-auto">
                        Edit Profile
                    </h1>
                ) : (
                    <h1 className="text-[30px] leading-[33px] font-normal font-young-serif text-[#3A1A1A] pt-8 text-center mx-auto">
                        Who's in
                        <p /> your fam?
                    </h1>
                )}
                <div className="w-6"></div>
            </div>

            <div className="flex flex-col gap-6 min-h-[325px]">
                {profiles.map((profile, index) => (
                    <div key={index} className="flex flex-col items-center">
                        <AvatarSlider
                            index={index}
                            onAvatarChange={handleAvatarChange}
                            centeredAvatars={centeredAvatars}
                            sliderRef={(el) => (sliderRefs.current[index] = el)}
                        />

                        <div className="mt-1 flex items-center size-full place-content-around">
                            <input
                                type="text"
                                value={profile.name}
                                maxLength={10}
                                onChange={(e) => setProfiles(prev => prev.map((p, i) =>
                                    i === index ? { ...p, name: e.target.value } : p
                                ))}
                                placeholder="Name"
                                className={`border rounded-full text-[16px] text-[#929292] font-space-grotesk font-normal w-[50%] py-2 text-center focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                    nameErrors[index] ? "border-red-500" : ""
                                }`}
                            />
                            <input
                                type="text"
                                value={profile.age}
                                onChange={(e) => handleAgeChange(e, index)}
                                placeholder="Age"
                                className={`border rounded-full w-[4rem] py-2 text-center text-[16px] text-[#929292] font-space-grotesk font-normal focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                    ageErrors[index] ? "border-red-500" : ""
                                }`}
                            />
                            <button
                                onClick={() => handleDeleteProfile(index)}
                                className="text-[14px] ml-2 underline underline-offset-6 text-[#929292]"
                            >
                                Delete profile
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <span className="text-red-500 text-center font-space-grotesk text-[14px]">
                {error && <p>{error}</p>}
                {ageErrors.map((ageError, index) =>
                    ageError ? (
                        <p className="font-space-grotesk" key={index}>Profile {index + 1}: {ageError}</p>
                    ) : null
                )}
            </span>

            <div>
                <button
                    onClick={handleAddProfile}
                    className="mt-6 mb-1 bg-black text-white py-6 px-6 rounded-full shadow-md flex items-center justify-center mx-auto"
                >
                    <IoAdd size={24} className="font-medium text-[#fff]" />
                </button>
                <p className="text-center text-[14px] text-[#1E0A1E] font-medium font-space-grotesk">
                    Add family member
                </p>
            </div>

            {!isEditMode && (
                <p className="text-center text-[16px] text-[#1E0A1E] font-normal font-space-grotesk leading-[20px]">
                    Add at least <span className="font-bold">TWO</span> people
                    <br />
                    and no more than <span className="font-bold">FIVE</span>
                </p>
            )}

            <div className="flex justify-center mt-8 mb-2">
                <button
                    onClick={handleSave}
                    className="font-space-grotesk bg-[#1E0A1E] text-[#EEEEEE] text-[16px] font-medium px-5 py-2.5 rounded-full hover:bg-gray-800 shadow-[2px_2px_4px_rgba(0,0,0,0.05),_5px_5px_2px_rgba(0,0,0,0.15)]"
                >
                    Save
                </button>
            </div>
        </div>
    );
};

export default AddFamily;